import {
  Button,
  Flex,
  type IconNames,
  MultiSelectFilterValue,
  StyledMultiSelectV2 as Select,
  Text,
} from '@kandji-inc/nectar-ui';
/* istanbul ignore file */
import * as React from 'react';
import { i18n } from 'src/i18n';
import { PlatformChip } from '../PlatformIcons';
import {
  type DeviceFilter,
  DeviceSelectValues,
  NextCheckedValues,
} from './DeviceFamilyFilter.types';
import { DeviceFilterTransformers } from './utils/DeviceFilterTransformers';
import { findFilter } from './utils/findAndUpdateFilter';

export interface DeviceFamilyTriggerProps {
  label: string;
  filters: DeviceFilter[];
  filtersApplied: boolean;
  filterLabels: string[];
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  updateFilters: (id: string, value: DeviceSelectValues) => void;
  disabled?: boolean;
  counterTooltip?: {
    content: React.ReactNode;
    icon?: IconNames;
    color?: string;
    side?: 'top' | 'bottom';
  };
}

export type TriggerWithChipsProp = DeviceFamilyTriggerProps & {
  label?: string | JSX.Element;
};

const DefaultTrigger = React.forwardRef(function DefaultTrigger(
  {
    filtersApplied,
    filterLabels,
    label,
    isOpen,
    disabled,
    counterTooltip,
    ...rest
  }: DeviceFamilyTriggerProps,
  ref,
) {
  return (
    <Select.Button
      variant={filtersApplied ? 'filterApplied' : 'filter'}
      compact
      css={{ height: 30 }}
      ref={ref}
      disabled={disabled}
      {...rest}
    >
      <MultiSelectFilterValue
        title={label}
        filters={filterLabels}
        counterTooltip={counterTooltip}
      />
      <Select.TriggerIcon
        name={isOpen ? 'fa-angle-up-small' : 'fa-angle-down-small'}
      />
    </Select.Button>
  );
});

export function PlatformTriggerChip({
  filters,
  filter,
  updateFilters,
}: {
  filters: DeviceFilter[];
  filter: DeviceFilter;
  updateFilters: (id: string, value: DeviceSelectValues) => void;
}) {
  if (filter.value === DeviceSelectValues.UNCHECKED) {
    return null;
  }

  if (filter.parent) {
    const parent = findFilter(filters, filter.parent);
    if (parent.value === DeviceSelectValues.CHECKED) return null;
  }

  const color = filter.parent != null ? 'unfilled' : 'neutral';

  return (
    <PlatformChip
      name={filter.icon}
      label={filter.label}
      chipProps={{
        color,
        dismissible: {
          onIconClick: (e) => {
            e.stopPropagation();
            updateFilters(filter.id, NextCheckedValues[filter.value]);
          },
        },
      }}
    />
  );
}

const TriggerWithChips = React.forwardRef(function TriggerWithChips(
  {
    filters,
    filtersApplied,
    setIsOpen,
    isOpen,
    updateFilters,
    disabled,
    label,
  }: TriggerWithChipsProp,
  ref: any,
) {
  const filterItems = DeviceFilterTransformers.toDeviceFilterItems({
    filters: filters,
    handlers: { updateFilters },
    component: PlatformTriggerChip,
  });

  const noFiltersAppliedLabel = label ?? i18n.t('Select device families');

  React.useEffect(() => {
    if (ref?.current) {
      document.documentElement.style.setProperty(
        '--trigger-width',
        `${ref.current.offsetWidth}px`,
      );
    }

    return () => {
      document.documentElement.style.removeProperty('--trigger-width');
    };
  }, [ref, isOpen]);

  return (
    <Button
      compact
      disabled={disabled}
      ref={ref}
      variant="input"
      onClick={() => {
        setIsOpen(!isOpen);
      }}
      css={{
        width: '100%',
        height: '36px',
        justifyContent: 'space-between',
      }}
    >
      <Flex alignItems="center" gap="xs">
        {filtersApplied ? (
          filterItems
        ) : (
          <Text variant="secondary">{noFiltersAppliedLabel}</Text>
        )}
      </Flex>
      <Select.TriggerIcon
        name={isOpen ? 'fa-angle-up-small' : 'fa-angle-down-small'}
      />
    </Button>
  );
});

export const DeviceFilterTriggers = {
  Default: DefaultTrigger,
  TriggerWithChips,
};
